import './index.css'
import clsx from 'clsx'
import cuid from 'cuid'

import React, { useContext, useEffect, useState } from 'react'
import { useSelector } from 'hooks/use-redux'
import type { ParentProps } from 'types/explorer'
import type { NestingLabel, Track } from 'types/track'
import { TRACK_NESTING_DND } from '@disco/constants/FeatureTypes'
import { FILE, TRACK } from '@disco/constants/ItemTypes'
import { STEM } from '@disco/constants/Nesting'
import { NestingVersionsProvider } from 'contexts/nesting-versions'
import { ShortlistContext } from 'contexts/shortlist'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import { getUploadingToExplorerFilesCount } from '@disco/state-management/selectors/explorer'
import { getNestingById } from '@disco/state-management/selectors/nesting'
import { getNestedTracksCount } from '@disco/utils/tracks'
import PaneItems from 'components/explorer/pane/pane-items'
import TrackVersionsButton from 'components/nesting/track-versions-button'
import type { TrackVersionsProps } from 'components/nesting/track-versions-container'
import CompactAccordion from 'components/ui/compact-accordion'
import PaneItemIndex from 'components/ui/pane-item-index'
import TrackDropWrapper from 'containers/TrackDropWrapper'
import TrackVersionsOverlay from './TrackVersionsOverlay'

const PaneItemsWithParentProps = ({
  parentProps,
  disabledDrop,
  disableSelection,
  items,
  trackVersionsButton,
  disableItemClick,
  disablePaneOpen,
  itemPrefix,
}: {
  parentProps: ParentProps
  disabledDrop?: boolean
  disableSelection?: boolean
  items: Track[]
  trackVersionsButton?: React.ReactNode
  disableItemClick?: boolean
  disablePaneOpen?: boolean
  itemPrefix?: string
}) => {
  const [id] = useState(cuid())
  const nestingDndEnabled = useFeatureEnabled(TRACK_NESTING_DND)
  // don't use Sortable with main track
  const withSortable =
    !trackVersionsButton && nestingDndEnabled
      ? {
          accept: [FILE, TRACK],
          id,
        }
      : undefined
  return (
    <PaneItems // parentProps
      loadNext={parentProps.loadNext}
      appendTracksToPlaylist={parentProps.appendTracksToPlaylist}
      componentHeight={parentProps.componentHeight}
      groups={parentProps.groups}
      isLoading={parentProps.isLoading}
      pane={parentProps.pane}
      selectedItems={parentProps.selectedItems}
      library={parentProps.library}
      addNestedTracksToMain={parentProps.addNestedTracksToMain}
      uploadFilesToMainTrack={parentProps.uploadFilesToMainTrack} // parentProps
      disabledDrop={disabledDrop}
      items={items}
      disableSelection={disableSelection}
      trackVersionsButton={trackVersionsButton}
      // @ts-expect-error
      withSortable={withSortable}
      disableItemClick={disableItemClick}
      disablePaneOpen={disablePaneOpen}
      itemPrefix={itemPrefix}
    />
  )
}

// TODO: move to components/nesting folder as it's being reused in other places
export const AccordionWrapper = ({
  mainTrack,
  parentProps,
  items,
  nestingLabel,
  title,
  open,
  disableItemClick,
  disablePaneOpen,
  itemPrefix,
}: {
  mainTrack: Track
  items: Track[]
  parentProps: ParentProps
  nestingLabel: NestingLabel
  title?: 'STEMS' | 'VERSIONS'
  open?: boolean
  disableItemClick?: boolean
  disablePaneOpen?: boolean
  itemPrefix?: string
}): React.ReactNode => {
  const [isOpen, setIsOpen] = useState(open)
  const shortlistContext = useContext(ShortlistContext)
  useEffect(() => {
    setIsOpen(open)
  }, [open])
  const accordionNodeLeft = Boolean(shortlistContext?.shortlistId) ? (
    <PaneItemIndex />
  ) : null
  return (
    <NestingVersionsProvider
      value={{
        nesting: true,
      }}
    >
      <TrackDropWrapper
        itemPrefix={itemPrefix}
        track={mainTrack}
        addNestedTracksToMain={parentProps.addNestedTracksToMain}
        uploadFilesToMainTrack={parentProps.uploadFilesToMainTrack}
        selectedItems={parentProps.selectedItems}
        nestingLabel={nestingLabel}
        onDragHold={() => setIsOpen(true)}
      >
        <CompactAccordion
          title={title}
          open={isOpen}
          onToggle={setIsOpen}
          nodeLeft={accordionNodeLeft}
        >
          <PaneItemsWithParentProps
            parentProps={parentProps}
            items={items}
            disabledDrop
            disableItemClick={disableItemClick}
            disablePaneOpen={disablePaneOpen}
            itemPrefix={itemPrefix}
          />
        </CompactAccordion>
      </TrackDropWrapper>
    </NestingVersionsProvider>
  )
}

const ExplorerTrackVersions = ({
  parentProps,
  mainTrack,
  currentTrack,
  versions,
  stems,
  currentTrackIndex,
}: TrackVersionsProps): null | React.ReactElement<'div'> => {
  // TODO: update design for drop tracks in empty category
  const [open, setOpen] = useState(false)
  const updating = Boolean(
    useSelector(state => getNestingById(state, mainTrack?.id)?.updating),
  )
  const tracksUploading = Boolean(useSelector(getUploadingToExplorerFilesCount))
  const count = getNestedTracksCount(mainTrack)
  const isMainVersion = currentTrack?.id === mainTrack?.id
  const mainTrackNode = (
    <TrackDropWrapper
      itemPrefix={`main-${currentTrackIndex || 0}`}
      track={mainTrack}
      addNestedTracksToMain={parentProps.addNestedTracksToMain}
      uploadFilesToMainTrack={parentProps.uploadFilesToMainTrack}
      selectedItems={parentProps.selectedItems}
      onDragHold={() => setOpen(true)}
    >
      <PaneItemsWithParentProps
        parentProps={parentProps}
        items={[mainTrack]}
        itemPrefix={`main-${currentTrackIndex || 0}`}
        trackVersionsButton={
          count > 0 && (
            <TrackVersionsButton
              onClick={() => setOpen(!open)}
              open={open}
              count={count}
            />
          )
        }
        disableSelection
        disabledDrop
      />
    </TrackDropWrapper>
  )
  const stemsNode = stems && (
    <AccordionWrapper
      mainTrack={mainTrack}
      parentProps={parentProps}
      items={stems}
      title="STEMS"
      nestingLabel={STEM}
      itemPrefix={`stems-${currentTrackIndex || 0}`}
    />
  )
  const overlay = (
    <TrackVersionsOverlay updating={updating} uploading={tracksUploading} />
  )

  if (!mainTrack) {
    return null
  }

  if (isMainVersion) {
    return (
      <div className="ExplorerTrackVersions">
        {overlay}
        <h2 className="ExplorerTrackVersions__CategoryTitle">Versions</h2>
        <div
          className={clsx({
            ExplorerTrackVersions__MainTrack: open,
          })}
        >
          {mainTrackNode}
        </div>
        <div
          className={clsx('ExplorerTrackVersions__CollapsibleContainer', {
            'ExplorerTrackVersions__CollapsibleContainer--Open': open,
          })}
        >
          {versions && (
            <AccordionWrapper
              mainTrack={mainTrack}
              parentProps={parentProps}
              items={versions}
              open={open}
              nestingLabel={null}
              itemPrefix={`versions-${currentTrackIndex || 0}`}
            />
          )}

          {open && stemsNode}
        </div>
      </div>
    )
  }

  return (
    <div className="ExplorerTrackVersions">
      {overlay}
      <h2 className="ExplorerTrackVersions__CategoryTitle">Version of</h2>
      <div
        className={clsx({
          ExplorerTrackVersions__MainTrack: open,
        })}
      >
        {mainTrackNode}
      </div>
      <div
        className={clsx('ExplorerTrackVersions__CollapsibleContainer', {
          'ExplorerTrackVersions__CollapsibleContainer--Open': open,
        })}
      >
        {open && (
          <>
            {versions && (
              <AccordionWrapper
                mainTrack={mainTrack}
                parentProps={parentProps}
                items={versions}
                title="VERSIONS"
                nestingLabel={null}
                open={open}
                itemPrefix={`versions-${currentTrackIndex || 0}`}
              />
            )}
            {stemsNode}
          </>
        )}
      </div>
    </div>
  )
}

export default ExplorerTrackVersions
